export const simulationConfig = {
  ticksPerSecond: 60,
  distanceEpsilon: 0.01,
  unitClearance: 20,
  maxPathSearchesPerOwner: 2,
  maxPathSearchesPerTick: 10,
  // World units (2 tiles). A unit reuses a path its owner found this tick when
  // both origins and both goal tiles fall inside this radius.
  pathShareRadius: 128,
  maxMissileStrikes: 4096,
  productionQueueLimit: 20,
  strikeRevealPadding: 64,
  strikeRevealRadiusMax: 8192,
  projectileHitRadius: 24,
  firingCandidates: 32,
  guardPursuitPadding: 96,
  firingRangeInset: 24,
  spawnExitPadding: 24,
  spawnOffset: 80,
  separationDistance: 20,
  separationMaxStep: 2,
  // SC2 magic box: a group ordered outside its own bounds keeps its layout.
  // Click margin around the group box, and the widest box (px) that still
  // counts as compact; a spread-out group converges on the point instead.
  groupBoxMargin: 32,
  groupBoxMaxSpan: 384,
  // Arrival without shoving: within this many px of its goal, a unit that
  // touches a settled own ground unit finishes the move where it stands.
  groupArrivalRadius: 48,
  fogRadiusCacheMax: 1024,
  // Positions snap to a 1/256 px lattice, ties to even. Team 1 computes its x
  // around a different base (2048 - x) than team 0, and that rounding differs
  // by one ulp per step. On the lattice both sides land on the same cell, so
  // mirrored matches stay bit-identical and no side gains a float edge.
  positionScale: 256,
  aircraftAltitude: 60,
  aircraftSeparationMinHeight: 50,
  aircraftGridSize: 64,
  aircraftSpacing: 56,
  droneSpacing: 32,
  formationLineWidthMax: 32,
  formationColumnWidth: 2,
  formationLineAspect: 3,
};

// packages/sim/src/state.ts
export const simStateConfig = {
  DT: 1 / 60,
  MIN_TEAMS: 2 as const,
  MAX_TEAMS: 8 as const,
  // Maximum queued orders per unit. Snapshot/restore supports exactly this many
  // entries, so simulation queues must never grow past it or a mid-match save
  // would fail to reload. Only append-style growth is gated: plain replacement
  // resets the queue and stays always allowed. The snapshot validator wires its
  // own cap to this constant, so renumbering here renumbers the contract.
  MAX_ORDER_QUEUE: 64 as const,
  DEFEND_PURSUIT: 320,
  AGGRESSIVE_PURSUIT: 640 as const,
};

// packages/sim/src/features/protection.ts
export const simFeaturesProtectionConfig = {
  MAX_REFERENCE_CHAIN: 64 as const,
};

// packages/sim/src/features/elimination-time.ts
export const simFeaturesEliminationTimeConfig = {
  // Matches the snapshot tick bound in ../snapshot.ts; repeated locally so the
  // feature stays independent of that module's private constants.
  MAX_TICK: 1_000_000_000 as const,
};

// packages/sim/src/features/battle-history.ts
export const simFeaturesBattleHistoryConfig = {
  MAX_TICK: 1_000_000_000 as const,
  MAX_EVENTS: 256 as const,
};

// packages/sim/src/features/cohorts.ts
export const simFeaturesCohortsConfig = {
  COHORT_STALL_TICKS: 120 as const,
  JOIN_TICKS: 120 as const,
  TOLERANCE: 96 as const,
  MAX_COHORTS: 1024 as const,
};

// packages/sim/src/features/patrol.ts
export const simFeaturesPatrolConfig = {
  MAX_PATROL_WAYPOINTS: 8 as const,
};

// packages/sim/src/combat.ts
export const simCombatConfig = {
  RESPONSE_RADIUS: 128 as const,
  RETURN_FIRE_PAUSE_TICKS: 60 as const,
  RETURN_FIRE_COOLDOWN_TICKS: 180 as const,
  BUNKER_RANGE_MULTIPLIER: 2,
  BUNKER_DAMAGE_MULTIPLIER: 1.25,
};

// packages/sim/src/entity-grid.ts
export const simEntityGridConfig = {
  CELL: 128 as const,
};

// packages/sim/src/snapshot.ts
export const simSnapshotConfig = {
  // Current flat snapshot format.
  SNAPSHOT_VERSION: 13 as const,
  // Heightfields carry terrain metadata in the landscape format.
  LANDSCAPE_SNAPSHOT_VERSION: 14 as const,
  // Pin gameplay data for deterministic snapshots and replays.
  SNAPSHOT_DATA_VERSION: 25 as const,
  MAX_ENTITIES: 16_384 as const,
  MAX_PROJECTILES: 4096 as const,
  MAX_DOCK_ENTRIES: 64 as const,
  MAX_PRODUCTION_QUEUE: 20 as const,
  MAX_ROUTE_POINTS: 1024 as const,
  MAX_TICK: 1_000_000_000 as const,
  MAX_ID: 1_000_000_000 as const,
  MIN_MAP_DIMENSION: 1 as const,
  MAX_MAP_POINTS: 64 as const,
};

// packages/sim/src/view-schema.ts
export const simViewSchemaConfig = {
  // --- Notifications ---
  // Data-only cap moved here to prevent circular imports between view.ts and view-schema.ts.
  VIEW_NOTIFICATIONS_MAX: 8 as const,
  // --- Entity Base / Remembered Buildings ---
  // Data-only cap moved here to prevent circular imports between view.ts and view-schema.ts.
  VIEW_REMEMBERED_BUILDINGS_MAX: 4_096 as const,
  // --- Battle History ---
  VIEW_BATTLE_EVENTS_MAX: 256 as const,
  V2_VIEW_MAX_STRING: 256 as const,
  V2_VIEW_MAX_PROJECTILES: 1_024 as const,
  // --- Transient Effects ---
  // Data-only cap moved here so the owning schemas carry every WorldView bound
  // (same rationale as the projectile caps above).
  V2_VIEW_MAX_EFFECTS: 1_024 as const,
  // --- WorldView Self & Complete WorldView ---
  V2_VIEW_MAX_ENTITIES: 16_384 as const,
  V2_VIEW_MAX_BUILD_OPTIONS: 64 as const,
  V2_VIEW_MAX_TRAIN_OPTIONS: 128 as const,
  V2_VIEW_MAX_OPTION_MISSING: 32 as const,
};

// packages/sim/src/commands.ts
export const simCommandsConfig = {
  // One typed versioned serializable command envelope for every gameplay mutation:
  // unit orders (with append intent), build (with append intent), train, stable-job
  // cancel, sell, rally, and stop. Convention: State T -> dispatch frame T ->
  // step T+1. Each envelope carries the tick it applies to and a per-team sequence;
  // gameplay legality still re-validates authoritatively inside sim at execution.
  MAX_COMMAND_BYTES: 16_384 as const,
  MAX_FRAME_COMMANDS: 256 as const,
};

// packages/sim/src/command-schema.ts
export const simCommandSchemaConfig = {
  COMMAND_VERSION: 1 as const,
  MAX_COMMAND_IDS: 256 as const,
  MAX_COMMAND_COORD: 1_000_000 as const,
  MAX_COMMAND_ID: 2_147_483_647 as const,
};

// packages/sim/src/formations.ts
export const simFormationsConfig = {
  DEFAULT_FORMATION: { shape: "free", spacing: "normal" } as const,
  FORMATION_GAPS: {
    compact: 32,
    normal: 48,
    wide: 72,
  },
};

// packages/sim/src/replay.ts
export const simReplayConfig = {
  REPLAY_VERSION: 10 as const,
  // Same cadence as the multiplayer hash plan: cheap enough live, dense enough
  // to bisect a desync.
  CHECKPOINT_EVERY: 60 as const,
  // Bounds command-frame count, not the elapsed replay tick span.
  MAX_REPLAY_FRAMES: 120_000 as const,
  // Bound untrusted replay CPU separately from the sparse command-frame count.
  // 1,000,000 ticks supports 4h 37m of playback at 60 Hz.
  MAX_REPLAY_TICKS: 1_000_000 as const,
  MAX_REPLAY_CHECKPOINTS: 4096 as const,
  MAX_REPLAY_COMMANDS: 1_000_000 as const,
};

// packages/sim/src/queue-tools.ts
export const simQueueToolsConfig = {
  MAX_ORDER_ID: 1_000_000_000 as const,
};

// packages/sim/src/settings.ts
export const simSettingsConfig = {
  SETTINGS_SCHEMA_VERSION: 1 as const,
  SETTINGS_DEFAULTS_VERSION: 24 as const,
};

// packages/sim/src/objective-kinds.ts
export const simObjectiveKindsConfig = {
  // Separate ownership: neutral objectives never occupy a player slot.
  NEUTRAL_OWNER: -1,
};
